/**
 * src/types/chart.config.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * Higher-level chart system — Firestore documents, agent types, validation,
 * animation style registry, and physics defaults.
 *
 * Does NOT define ChartType, AnyDataPoint, SpacingConfig, or ChartConfig —
 * those live in chart.registry.ts to avoid circular dependencies.
 *
 * Chart2Video | CORSphere Software Inc.
 * Schema version: 3
 */

import type { ChartType, ChartConfig, AnyDataPoint, AnimationStyle, SpacingConfig } from './chart.registry';
import { VALID_CHART_TYPES, VALID_ANIMATION_STYLES, CHART_DEFAULTS } from './chart.registry';
import type { ContentConfig, AnimationConfig, BackgroundConfig, ChartLayoutConfig, AxesConfig, LegendConfig, DataLabelConfig, ExportConfig, AnimationStyleDef, AnimationPhysics } from './core/interfaces.shared';
import { CANVAS_DIMS } from './core/primitives';
import type { Annotation } from './core/annotation.types';


// =============================================================================
// CONSTANTS
// =============================================================================

export const SCHEMA_VERSION = 3;

export const VALID_QUALITIES = ['480p', '720p', '1080p', '1440p', '4k'] as const;

/**
 * Default physics for AnimationPhysics.
 * Used by ElementPanel to initialise physics sliders and as reset targets.
 * Must stay in sync with defaults in each chart renderer's physics constants.
 */
export const DEFAULT_PHYSICS = {
  bounce:        0.35,   // subtle natural spring — maps to damping=0.685
  springFreq:    2.5,
  staggerSpread: 0.45,
} as const;


// =============================================================================
// FIRESTORE DOCUMENT TYPES
// =============================================================================

export type GenerationSource = 'human' | 'agent' | 'agent-edited';

export interface QualitySignals {
  exported:       boolean;
  exportCount:    number;
  iterated:       boolean;    // draft saved 2+ times before export
  abandoned:      boolean;    // saved, never exported, not opened in 7+ days
  agentGenerated: boolean;
}

export interface SavedChartMeta {
  userId:           string;
  chartType:        ChartType;
  title:            string;           // convenience copy of content.titles[0].text
  createdAt:        number;           // Unix ms
  updatedAt:        number;
  schemaVersion:    number;
  generationSource: GenerationSource;
}

export interface SavedChartDocument {
  id:          string;
  meta:        SavedChartMeta;
  config:      ChartConfig;
  data:        AnyDataPoint[];
  annotations: Annotation[];
  signals:     QualitySignals;
}


// =============================================================================
// AGENT TYPES
// =============================================================================

export interface ChartIntent {
  chartType:        ChartType;
  dataDescription:  string;
  styleHints:       string[];
  contentHints:     { title?: string; subtitle?: string };
}

export interface ChartDiff {
  content?:    Partial<ContentConfig>;
  animation?:  Partial<AnimationConfig>;
  background?: Partial<BackgroundConfig>;
  layout?:     Partial<ChartLayoutConfig>;
  axes?:       Partial<AxesConfig>;
  legend?:     Partial<LegendConfig>;
  dataLabels?: Partial<DataLabelConfig>;
  spacing?:    Partial<SpacingConfig>;
  export?:     Partial<ExportConfig>;
}


// =============================================================================
// VALIDATION
// =============================================================================

/**
 * Lightweight structural pre-check.
 * Returns an array of error strings, or null if the config is clean.
 * Does NOT validate semantic correctness or aesthetic quality.
 * For the full agent pipeline gate, use validateChartConfig() in validateConfig.ts.
 */
export function quickValidateChartConfig(config: ChartConfig): string[] | null {
  const errors: string[] = [];

  if (!VALID_CHART_TYPES.includes(config.chartType)) {
    errors.push(`Invalid chartType: '${config.chartType}'. Must be one of: ${VALID_CHART_TYPES.join(', ')}.`);
  }

  if (!config.content?.titles?.length) {
    errors.push('content.titles must have at least one entry.');
  }

  if (config.animation) {
    const { duration, style } = config.animation;
    if (typeof duration === 'number' && (duration < 0.5 || duration > 20)) {
      errors.push(`animation.duration must be 0.5–20. Got: ${duration}.`);
    }
    const validStyles = VALID_ANIMATION_STYLES[config.chartType] ?? [];
    if (style && !validStyles.includes(style)) {
      errors.push(`animation.style '${style}' is not valid for '${config.chartType}'. Valid: ${validStyles.join(', ')}.`);
    }
  }

  if (config.background) {
    const { opacity, contrast } = config.background;
    if (typeof opacity === 'number' && (opacity < 0 || opacity > 1)) {
      errors.push(`background.opacity must be 0–1. Got: ${opacity}.`);
    }
    if (typeof contrast === 'number' && (contrast < 0.2 || contrast > 2.0)) {
      errors.push(`background.contrast must be 0.2–2.0. Got: ${contrast}.`);
    }
  }

  if (config.spacing?.chartType !== config.chartType) {
    errors.push(`spacing.chartType '${config.spacing?.chartType}' does not match top-level chartType '${config.chartType}'.`);
  }

  if (config.export?.quality && !(VALID_QUALITIES as readonly string[]).includes(config.export.quality)) {
    errors.push(`export.quality must be one of: ${VALID_QUALITIES.join(', ')}. Got: '${config.export.quality}'.`);
  }

  // Derive valid formats from CANVAS_DIMS — single source of truth.
  const VALID_FORMATS = Object.keys(CANVAS_DIMS);
  if (config.export?.canvasFormat && !VALID_FORMATS.includes(config.export.canvasFormat)) {
    errors.push(`export.canvasFormat must be one of: ${VALID_FORMATS.join(', ')}. Got: '${config.export.canvasFormat}'.`);
  }

  return errors.length > 0 ? errors : null;
}

/**
 * Merges a ChartDiff onto a ChartConfig. Does not mutate the original.
 * Agents and the autosave system call this after producing a diff.
 */
export function applyChartDiff(base: ChartConfig, diff: ChartDiff): ChartConfig {
  return {
    ...base,
    ...(diff.content    && { content:    { ...base.content,    ...diff.content    } }),
    ...(diff.animation  && { animation:  { ...base.animation,  ...diff.animation  } }),
    ...(diff.background && { background: { ...base.background, ...diff.background } }),
    ...(diff.layout     && { layout:     { ...base.layout,     ...diff.layout     } }),
    ...(diff.axes       && { axes:       { ...base.axes,       ...diff.axes       } as AxesConfig }),
    ...(diff.legend     && { legend:     { ...base.legend,     ...diff.legend     } }),
    ...(diff.dataLabels && { dataLabels: { ...base.dataLabels, ...diff.dataLabels } }),
    ...(diff.spacing    && { spacing:    { ...base.spacing,    ...diff.spacing    } as SpacingConfig }),
    ...(diff.export     && { export:     { ...base.export,     ...diff.export     } }),
  };
}


// =============================================================================
// ANIMATION STYLE REGISTRY
// ─────────────────────────────────────────────────────────────────────────────
// Single source of truth for documentation, agent prompts, and UI dropdowns.
//
// ADDING A NEW STYLE — complete checklist:
//   1. Add the style string to the relevant *AnimationStyle union in its chart module.
//   2. Add it to VALID_ANIMATION_STYLES in chart.registry.ts (Step E).
//   3. Implement it in the chart renderer's animationStyleImpls.
//   4. Add an entry to ANIMATION_STYLE_REGISTRY below.
//   5. Add a SchemaNote entry in chart.schema.ts.
// =============================================================================

export const ANIMATION_STYLE_REGISTRY: AnimationStyleDef[] = [
  // ── Bar ────────────────────────────────────────────────────────────────────
  { value: 'classic',    label: 'Classic',        charts: ['bar', 'bar-horizontal', 'bar-grouped'],
    physicsCategory: 'smooth',     loopFriendly: true,
    description: 'All bars rise together with a tiny stagger using easeOutCubic — smooth and professional.' },
  { value: 'sequential', label: 'Sequential',     charts: ['bar', 'bar-horizontal', 'bar-stacked', 'bar-grouped'],
    physicsCategory: 'staggered',  loopFriendly: false,
    description: 'Bars enter one-by-one left to right, each using easeOutCubic within its own window.' },
  { value: 'staggered',  label: 'Staggered',      charts: ['bar', 'bar-horizontal'],
    physicsCategory: 'staggered',  loopFriendly: false,
    description: 'Cascading stagger with 40% spread — leading bars are well ahead of trailing ones.' },
  { value: 'staggered',  label: 'Staggered',      charts: ['bar-grouped'],
    physicsCategory: 'staggered',  loopFriendly: false,
    description: 'Groups arrive one by one; within each group, bars fire in rapid half-stagger sequence. The signature grouped bar style.' },
  { value: 'elastic',    label: 'Elastic',        charts: ['bar', 'bar-horizontal', 'bar-stacked'],
    physicsCategory: 'expressive', loopFriendly: false,
    description: 'Bars spring in with 12% overshoot then settle — each feels like it has mass and momentum.' },
  { value: 'wave',       label: 'Wave',           charts: ['bar', 'bar-horizontal', 'bar-grouped'],
    physicsCategory: 'wave',       loopFriendly: true,
    description: 'Sinusoidal ripple modulates bar heights as global easing rises — organic and rhythmic.' },
  { value: 'reveal',     label: 'Reveal',         charts: ['bar', 'bar-horizontal', 'line'],
    physicsCategory: 'spring',     loopFriendly: false,
    description: 'A spotlight sweeps left to right; each bar springs in with springSnappy as the spotlight passes.' },
  { value: 'fadeScale',  label: 'Fade + Scale',   charts: ['bar', 'bar-horizontal', 'bar-stacked', 'line'],
    physicsCategory: 'staggered',  loopFriendly: false,
    description: 'Bars fade in and scale up simultaneously with a 30% spread stagger.' },
  { value: 'popIn',      label: 'Pop In',         charts: ['bar', 'bar-horizontal', 'bar-stacked', 'pie', 'scatter'],
    physicsCategory: 'expressive', loopFriendly: false,
    description: 'Three-phase entry: fast rise to 112% then spring back to 100%. Punchy and confident.' },
  { value: 'growUp',     label: 'Grow Up',        charts: ['bar-stacked'],
    physicsCategory: 'smooth',     loopFriendly: false,
    description: 'Stacked bars grow up from baseline with segments revealing sequentially from bottom to top.' },
  { value: 'layerByLayer', label: 'Layer by Layer', charts: ['bar-stacked'],
    physicsCategory: 'spring', loopFriendly: false,
    description: 'All bottom segments across all columns appear first, then next layer across all columns. Composition reveal.' },
  { value: 'buildUp', label: 'Build Up', charts: ['bar-stacked'],
    physicsCategory: 'smooth', loopFriendly: false,
    description: 'Within each column, segments stack bottom to top sequentially. All columns build simultaneously.' },
  { value: 'diagonal', label: 'Diagonal', charts: ['bar-stacked'],
    physicsCategory: 'spring', loopFriendly: false,
    description: 'Diagonal wave from bottom-left to top-right across the category-series grid.' },

  // ── Line ───────────────────────────────────────────────────────────────────
  { value: 'classic',         label: 'Classic',           charts: ['line'],
    physicsCategory: 'smooth',     loopFriendly: true,
    description: 'Line draws left to right with easeInOutCubic; dots appear as the line reaches each point.' },
  { value: 'drawLine',        label: 'Draw Line',         charts: ['line'],
    physicsCategory: 'smooth',     loopFriendly: false,
    description: 'Identical to classic — line traces across the canvas at a smooth accelerating pace.' },
  { value: 'sequential',      label: 'Sequential',        charts: ['line'],
    physicsCategory: 'expressive', loopFriendly: false,
    description: 'Points spring in one-by-one with springExpressive, then the line fades in.' },
  { value: 'trace',           label: 'Tracer',            charts: ['line'],
    physicsCategory: 'physics',    loopFriendly: false,
    description: 'Ghost line shows immediately; a glowing tracer travels the path with a radial light effect.' },
  { value: 'growFromAxis',    label: 'Grow From Axis',    charts: ['line'],
    physicsCategory: 'smooth',     loopFriendly: false,
    description: 'All elements appear at once; axes grow up from the baseline together.' },
  { value: 'growFromAxis',    label: 'Grow From Axis',    charts: ['bar-grouped'],
    physicsCategory: 'spring',     loopFriendly: true,
    description: 'All bars in all groups rise simultaneously from the X-axis baseline with a gentle spring settle.' },
  { value: 'segmentBySegment', label: 'Segment by Segment', charts: ['line'],
    physicsCategory: 'smooth',     loopFriendly: false,
    description: 'Each line segment appears sequentially — crisp binary reveal, no easing within segments.' },
  { value: 'pulse',           label: 'Pulse',             charts: ['line'],
    physicsCategory: 'wave',       loopFriendly: true,
    description: 'A sine-wave envelope propagates across points, fading in and out rhythmically.' },
  { value: 'cascade',         label: 'Cascade',           charts: ['line', 'heatmap'],
    physicsCategory: 'staggered',  loopFriendly: false,
    description: 'Line draws first; each dot drops in from above with a physical bounce momentum.' },
  { value: 'cascade',         label: 'Cascade',           charts: ['bar-grouped'],
    physicsCategory: 'staggered',  loopFriendly: false,
    description: 'Series-first reveal: Revenue appears across all groups, then Expenses, then Profit. Tells the series story.' },

  // ── Pie ────────────────────────────────────────────────────────────────────
  { value: 'classic',        label: 'Classic',           charts: ['pie'],
    physicsCategory: 'smooth',     loopFriendly: false,
    description: 'Slices sweep in clockwise, each starting when the previous ends.' },
  { value: 'sequential',     label: 'Sequential',        charts: ['pie'],
    physicsCategory: 'smooth',     loopFriendly: false,
    description: 'Sequential clockwise sweep — identical to classic for pie.' },
  { value: 'growFromCenter', label: 'Grow from Center',  charts: ['pie'],
    physicsCategory: 'spring',     loopFriendly: false,
    description: 'All slices grow outward from the center simultaneously with springGentle.' },
  { value: 'spinIn',         label: 'Spin In',           charts: ['pie'],
    physicsCategory: 'smooth',     loopFriendly: false,
    description: 'The whole pie spins in from a two-rotation entry with easeInOutCubic deceleration.' },
  { value: 'unfold',         label: 'Unfold',            charts: ['pie'],
    physicsCategory: 'smooth',     loopFriendly: false,
    description: 'Slices unfold outward from 12 oclock — each starts collapsed and opens like a fan.' },
  { value: 'separation',     label: 'Separation',        charts: ['pie'],
    physicsCategory: 'smooth',     loopFriendly: true,
    description: 'Fully formed pie; slices slide outward from center as if being pushed apart.' },
  { value: 'wipe',           label: 'Wipe',              charts: ['pie'],
    physicsCategory: 'smooth',     loopFriendly: false,
    description: 'A clockwise wipe reveals each slice; partial slices are drawn mid-reveal.' },

  // ── Heatmap ────────────────────────────────────────────────────────────────
  { value: 'wave',           label: 'Wave',              charts: ['heatmap'],
    physicsCategory: 'wave',       loopFriendly: true,
    description: 'Diagonal wave propagates across cells using easeInOutCubic within each cells window.' },
  { value: 'fadeIn',         label: 'Fade In',           charts: ['heatmap', 'radar'],
    physicsCategory: 'smooth',     loopFriendly: false,
    description: 'All cells fade in simultaneously with easeOutCubic — simple and clean.' },
  { value: 'rowByRow',       label: 'Row by Row',        charts: ['heatmap'],
    physicsCategory: 'spring',     loopFriendly: false,
    description: 'Rows enter top to bottom sequentially, each using springGentle.' },
  { value: 'columnByColumn', label: 'Column by Column',  charts: ['heatmap'],
    physicsCategory: 'spring',     loopFriendly: false,
    description: 'Columns enter left to right sequentially, each using springGentle.' },
  { value: 'zoom',           label: 'Zoom Out',          charts: ['heatmap'],
    physicsCategory: 'expressive', loopFriendly: false,
    description: 'Cells pop in from the center outward with springExpressive — dramatic center-first reveal.' },

  // ── Scatter ────────────────────────────────────────────────────────────────
  { value: 'fadeIn',      label: 'Fade In',   charts: ['scatter'],
    physicsCategory: 'smooth',     loopFriendly: false,
    description: 'All points fade in simultaneously with easeOutCubic.' },
  { value: 'sequential',  label: 'Sequential', charts: ['scatter'],
    physicsCategory: 'staggered',  loopFriendly: false,
    description: 'Points appear one-by-one in data order.' },
  { value: 'wave',        label: 'Wave',       charts: ['scatter'],
    physicsCategory: 'wave',       loopFriendly: true,
    description: 'Sine-wave envelope propagates across points.' },
  { value: 'ripple',      label: 'Ripple',     charts: ['scatter'],
    physicsCategory: 'expressive', loopFriendly: false,
    description: 'Circular wave radiates from chart centre — points appear as wave reaches their distance.' },
  { value: 'drift',       label: 'Drift Up',   charts: ['scatter'],
    physicsCategory: 'smooth',     loopFriendly: false,
    description: 'Points start at baseline Y and float upward to their actual position with smooth physics.' },

  // ── Gauge ──────────────────────────────────────────────────────────────────
  { value: 'sweep',   label: 'Sweep',   charts: ['gauge'],
    physicsCategory: 'smooth',     loopFriendly: false,
    description: 'Needle sweeps from minValue to the target reading with easeInOutCubic.' },
  { value: 'bounce',  label: 'Bounce',  charts: ['gauge'],
    physicsCategory: 'expressive', loopFriendly: false,
    description: 'Needle springs past the target value then settles back — springExpressive.' },

  // ── Bar Race ────────────────────────────────────────────────────────────────
  { value: 'smooth',    label: 'Smooth',    charts: ['bar-race'],
    physicsCategory: 'smooth',     loopFriendly: true,
    description: 'Bars glide linearly between frames — continuous, fluid motion.' },
  { value: 'snap',      label: 'Snap',      charts: ['bar-race'],
    physicsCategory: 'expressive', loopFriendly: true,
    description: 'Bars hold for 30% of each transition then ease out to new ranks — easier to read exact values.' },
  { value: 'drift',     label: 'Drift',     charts: ['bar-race'],
    physicsCategory: 'smooth',     loopFriendly: true,
    description: 'Bars decelerate gracefully into position — sliding into place like results being tabulated.' },
  { value: 'cinematic', label: 'Cinematic', charts: ['bar-race'],
    physicsCategory: 'expressive', loopFriendly: true,
    description: 'Bars hold for 35% then rush to new positions simultaneously — dramatic reveal effect.' },

  // ── Future charts (stubs, renderer not yet implemented) ───────────────────
  { value: 'classic',    label: 'Classic',    charts: ['radar', 'waterfall', 'funnel'],
    physicsCategory: 'smooth',     loopFriendly: false,
    description: 'Standard entry animation for this chart type.' },
  { value: 'sequential', label: 'Sequential', charts: ['waterfall', 'funnel'],
    physicsCategory: 'staggered',  loopFriendly: false,
    description: 'Elements enter one-by-one in data order.' },

  // ── Bar Horizontal — unique styles ──────────────────────────────────────
  { value: 'rankReveal', label: 'Rank Reveal', charts: ['bar-horizontal'],
    physicsCategory: 'spring',     loopFriendly: false,
    description: 'Countdown reveal — bottom bar (last place) appears first, winner last. Journalism idiom.' },
  { value: 'slideIn',    label: 'Slide In',    charts: ['bar-horizontal'],
    physicsCategory: 'expressive', loopFriendly: false,
    description: 'Bars snap in from the left with spring overshoot then settle at final width.' },
  { value: 'sweep',      label: 'Sweep',       charts: ['bar-horizontal'],
    physicsCategory: 'spring',     loopFriendly: false,
    description: 'Vertical scanner passes top to bottom — each bar springs out as the scanner reaches its row.' },

  // ── New styles (ANIM-1 Phase 3) ──────────────────────────────────────────
  { value: 'magnitudeStagger', label: 'Magnitude Stagger', charts: ['bar', 'bar-horizontal'],
    physicsCategory: 'expressive', loopFriendly: false,
    description: 'Larger bars animate last with more energy — builds to the chart climax.' },
  { value: 'writeOn',   label: 'Write On',    charts: ['line'],
    physicsCategory: 'smooth',     loopFriendly: false,
    description: 'Line draws itself left-to-right like a pen; each point snaps in as the tip arrives.' },
  { value: 'riseUp',    label: 'Rise Up',     charts: ['line'],
    physicsCategory: 'smooth',     loopFriendly: true,
    description: 'All points and area fill rise simultaneously from the baseline.' },
  { value: 'clusterReveal', label: 'Cluster Reveal', charts: ['scatter'],
    physicsCategory: 'expressive', loopFriendly: false,
    description: 'Points appear by spatial quadrant — bottom-left cluster first, top-right last.' },
  { value: 'valueOrder', label: 'Value Order', charts: ['pie'],
    physicsCategory: 'spring',     loopFriendly: false,
    description: 'Smallest slices appear first, largest slice arrives last — builds to the climax.' },
];
